import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const EMPTY = {
  operator: "", minAmount: "", maxAmount: "",
  agentChargeType: "FLAT", agentChargeValue: "",
  agentCommType: "FLAT", agentCommValue: "",
  distCommType: "FLAT", distCommValue: "",
  mdCommType: "FLAT", mdCommValue: "",
  merchantTdsPercentage: 0, distTdsPercentage: 2, mdTdsPercentage: 2,
};

const inp = "w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition";

const Field = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</label>
    <input type="number" name={name} value={value} onChange={onChange} className={inp} />
  </div>
);

const TypeSel = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</label>
    <select name={name} value={value} onChange={onChange} className={inp}>
      <option value="FLAT">FLAT</option>
      <option value="PERCENTAGE">PERCENTAGE</option>
    </select>
  </div>
);

const Section = ({ title, color, children }) => (
  <div className={`rounded-2xl overflow-hidden border ${color.border}`}>
    <div className={`px-4 py-2.5 ${color.bg}`}>
      <p className={`text-xs font-bold uppercase tracking-widest ${color.text}`}>{title}</p>
    </div>
    <div className="p-4 bg-white">{children}</div>
  </div>
);

const COLORS = {
  gray: { border: "border-gray-200", bg: "bg-gray-100", text: "text-gray-500" },
  orange: { border: "border-orange-200", bg: "bg-orange-50", text: "text-orange-600" },
  green: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-600" },
  blue: { border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-600" },
  purple: { border: "border-violet-200", bg: "bg-violet-50", text: "text-violet-600" },
};

const OPERATORS = [
  { label: "JIO", value: "JIO Mobile Recharge" },
  { label: "AIRTEL", value: "AIRTEL Mobile Recharge" },
  { label: "VI", value: "VI Mobile Recharge" },
  { label: "BSNL", value: "BSNL Mobile Recharge" },

  { label: "ELECTRICITY", value: "Electricity Bill" },
  { label: "GAS", value: "Gas Bill" },
  { label: "WATER", value: "Water Bill" },
  { label: "INSURANCE", value: "Insurance Bill" },
];

const BulkUpdateForm = ({ serviceType, users, slab, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState(EMPTY);

  const { callApi: bulkUpdate } = useApi("/commission/rates/bulk-update", "POST");

  useEffect(() => {
    if (slab) {
      setFormData({
        operator: slab.operator || "",
        minAmount: slab.minAmount ?? "", maxAmount: slab.maxAmount ?? "",
        agentChargeType: slab.agentChargeType || "FLAT", agentChargeValue: slab.agentChargeValue ?? "",
        agentCommType: slab.agentCommType || "FLAT", agentCommValue: slab.agentCommValue ?? "",
        distCommType: slab.distCommType || "FLAT", distCommValue: slab.distCommValue ?? "",
        mdCommType: slab.mdCommType || "FLAT", mdCommValue: slab.mdCommValue ?? "",
        merchantTdsPercentage: slab.merchantTdsPercentage ?? 0,
        distTdsPercentage: slab.distTdsPercentage ?? 2,
        mdTdsPercentage: slab.mdTdsPercentage ?? 2,
      });
    }
  }, [slab]);

  const handle = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); setError("");
      await bulkUpdate({
        serviceType,
        userType: users?.[0]?.userType,
        operator: formData.operator || null,
        userIds: users.map((u) => u.userId),
        rateTier: {
          minAmount: Number(formData.minAmount), maxAmount: Number(formData.maxAmount),
          agentChargeType: formData.agentChargeType, agentChargeValue: Number(formData.agentChargeValue),
          agentCommType: formData.agentCommType, agentCommValue: Number(formData.agentCommValue),
          distCommType: formData.distCommType, distCommValue: Number(formData.distCommValue),
          mdCommType: formData.mdCommType, mdCommValue: Number(formData.mdCommValue),
          merchantTdsPercentage: Number(formData.merchantTdsPercentage),
          distTdsPercentage: Number(formData.distTdsPercentage),
          mdTdsPercentage: Number(formData.mdTdsPercentage),
        },
      });
      setSuccess(true);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.message || "Bulk update failed.");
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center text-3xl">✓</div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Bulk Update Done!</h2>
      <p className="text-gray-400 text-sm mb-5">{users.length} user{users.length > 1 ? "s" : ""} updated</p>
      <div className="flex flex-wrap gap-2 justify-center">
        {users.map((u) => (
          <span key={u.userId} className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-xl text-xs font-semibold">{u.userId}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 text-base font-bold flex-shrink-0">⊞</div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Bulk Update Rates</h2>
          <p className="text-xs text-gray-400">
            Applying to <span className="font-semibold text-gray-600">{users.length} {users?.[0]?.userType || ""}</span> users · {serviceType}
          </p>
        </div>
      </div>

      {/* Selected users */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-5 flex flex-wrap gap-2">
        {users.map((u) => (
          <span key={u.userId} className="bg-white text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg text-xs font-semibold">{u.userId}</span>
        ))}
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-3">

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">User Type</label>
            <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-sm text-gray-500 font-semibold">{users?.[0]?.userType || "—"}</div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Operator (optional)
            </label>

            <select
              name="operator"
              value={formData.operator}
              onChange={handle}
              className={inp}
            >
              <option value="">Select Operator</option>

              {OPERATORS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Section title="Amount Range" color={COLORS.gray}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min Amount (₹)" name="minAmount" value={formData.minAmount} onChange={handle} />
            <Field label="Max Amount (₹)" name="maxAmount" value={formData.maxAmount} onChange={handle} />
          </div>
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Section title="Agent Charge" color={COLORS.orange}>
            <div className="space-y-2">
              <Field label="Value" name="agentChargeValue" value={formData.agentChargeValue} onChange={handle} />
              <TypeSel label="Type" name="agentChargeType" value={formData.agentChargeType} onChange={handle} />
            </div>
          </Section>
          <Section title="Agent Commission" color={COLORS.green}>
            <div className="space-y-2">
              <Field label="Value" name="agentCommValue" value={formData.agentCommValue} onChange={handle} />
              <TypeSel label="Type" name="agentCommType" value={formData.agentCommType} onChange={handle} />
            </div>
          </Section>
          <Section title="Distributor Commission" color={COLORS.blue}>
            <div className="space-y-2">
              <Field label="Value" name="distCommValue" value={formData.distCommValue} onChange={handle} />
              <TypeSel label="Type" name="distCommType" value={formData.distCommType} onChange={handle} />
            </div>
          </Section>
          <Section title="MD Commission" color={COLORS.purple}>
            <div className="space-y-2">
              <Field label="Value" name="mdCommValue" value={formData.mdCommValue} onChange={handle} />
              <TypeSel label="Type" name="mdCommType" value={formData.mdCommType} onChange={handle} />
            </div>
          </Section>
        </div>

        <Section title="TDS Percentages" color={COLORS.gray}>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Merchant %" name="merchantTdsPercentage" value={formData.merchantTdsPercentage} onChange={handle} />
            <Field label="Distributor %" name="distTdsPercentage" value={formData.distTdsPercentage} onChange={handle} />
            <Field label="MD %" name="mdTdsPercentage" value={formData.mdTdsPercentage} onChange={handle} />
          </div>
        </Section>

        <button type="submit" disabled={loading}
          className={`w-full py-3.5 rounded-xl text-white font-bold text-sm transition shadow-sm ${loading ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
          {loading ? "Updating..." : `Bulk Update ${users.length} User${users.length > 1 ? "s" : ""}`}
        </button>
      </form>
    </div>
  );
};

export default BulkUpdateForm;
